import {
  Box,
  CalendarIcon,
  ClockIcon,
  DoctorIcon,
  LuxButton,
  LuxSelectablePill,
  LuxTypography,
  useMediaQuery,
  UserIcon,
  useTheme,
} from '@gemini/fe-ds-core';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  AddressCtasWrapper,
  AddressSection,
  BookingCard,
  BookingDetailsContainer,
  ButtonLabelWrapper,
  ButtonWrapper,
  CardTitleWrapper,
  ColumnWrapper,
  DetailSection,
  InsPtCtDetailsWrapper,
  ItemWrapper,
  PatientPill,
  ScheduleDetailsWrapper,
  ScheduleWrapper,
  SubSectionTitle,
} from './BookingDetails.styles';
import { OnlineBookingStore } from '../../store/onlineBookingStore';
import { Patient, PatientStore } from '@store/patientStore';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { StepsStep } from '@utils/enums/StepsStep.enum';
import { useHistory } from 'react-router-dom';
import { PatientDetailsSection } from './sections/PatientDetails';
import { formatGeneralAddress, formatPhone, handlePrint } from './utils';
import { InsuranceSection } from './sections/Insurance';
import MoreInformationModal from '@components/MoreInformationModal/MoreInformationModal';
import { generateSortedPatientsList } from '@utils/functions';
import { useUrlsContext } from '@utils/contexts/UrlsContext';
import useGetTextColor from '@hooks/useGetTextColor';
import { MobileBreakpoint } from '@utils/consts/breakpoints.consts';
//ICONS
import Icon_CL from '@assets/icons/icon_CL.svg';
import Icon_EEX from '@assets/icons/icon_EEX.svg';

interface DetailsProps {
  patientStore: PatientStore;
  onlineBookingStore: OnlineBookingStore;
  isFullRecap?: boolean;
  hideEditButton?: boolean;
  checkAppId?: boolean;
}

const BookingDetailsTyp = ({
  patientStore,
  onlineBookingStore,
  isFullRecap,
  hideEditButton = false,
  checkAppId = false,
}: DetailsProps) => {
  const { t } = useTranslation();
  const { Urls } = useUrlsContext();
  const { getTextColor } = useGetTextColor();
  const history = useHistory();
  const theme = useTheme();
  const setFormStep = onlineBookingStore.setFormStep;
  const currentStep = onlineBookingStore.currentFormStep;
  const generalData = onlineBookingStore.generalInfo;
  const isRescheduling = onlineBookingStore.isRescheduling;
  const showDoctorDetails =
    onlineBookingStore.scheduleConfig?.showDoctorDetailsForWebScheduling ||
    false;

  const brandName = onlineBookingStore.brandName;

  const patients: Array<Patient | undefined> = patientStore.patients;

  const contactsDetails = patientStore.contactsDetails;
  const currentPatient = onlineBookingStore.currentPatient;
  const showAddPatientFrame = onlineBookingStore.showAddPatientFrame;

  const showInsuranceSection =
    !!onlineBookingStore.formConfigs?.askForInsuranceInformation;

  const insuranceDrivesAvailability =
    showInsuranceSection &&
    onlineBookingStore.formConfigs?.insuranceMustDriveAvailability === true;
  const insuranceEditUrl = insuranceDrivesAvailability
    ? Urls.SCHEDULE_EXAM
    : Urls.PATIENT_DETAILS;
  const insuranceEditStep = insuranceDrivesAvailability
    ? StepsStep.SCHEDULE_EXAM
    : StepsStep.PATIENT_DETAILS;

  const handlePrintClick = () => {
    handlePrint();
  };

  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isMobile = useMediaQuery(`(max-width:${MobileBreakpoint}px)`);

  const onlyTruePatients = useMemo(() => {
    if (checkAppId) {
      console.log('#onlyTruePatients.checkAppId');
      return generateSortedPatientsList(
        patients.filter(p => p?.appointmentId),
        t,
      );
    } else {
      return generateSortedPatientsList(patients, t);
    }
  }, [patients, checkAppId]);

  // console.log('#onlytrue patietns', onlyTruePatients);

  const [isMoreInfoOpen, setIsMoreInfoOpen] = useState(false);

  const getPatientName = useCallback(
    (index: number) => onlyTruePatients[index]?.content,
    [onlyTruePatients],
  );

  const onEditClick = (url: string, step: StepsStep) => {
    history.push(`${url}`);
    setFormStep(step);
  };

  const checkIfLastSection = useCallback(
    (step: StepsStep) => {
      // insurance step included
      switch (step) {
        case StepsStep.APPOINTMENT_TYPE:
          return !onlyTruePatients.some(
            p =>
              (p.patient?.insurance && showInsuranceSection) ||
              p.patient?.scheduleExam ||
              p.patient?.patientDetails?.firstName,
          );
        case StepsStep.INSURANCE:
          return !onlyTruePatients.some(
            p =>
              p.patient?.scheduleExam || p.patient?.patientDetails?.firstName,
          );
        case StepsStep.SCHEDULE_EXAM:
          return !onlyTruePatients.find(
            p => p.patient?.patientDetails?.firstName,
          );
        case StepsStep.PATIENT_DETAILS:
          return !contactsDetails?.firstName;
        default:
          return false;
      }
    },
    [contactsDetails, onlyTruePatients, showInsuranceSection],
  );

  const appTypeIcon = useMemo(() => {
    const icon: Partial<Record<string, ReactNode>> = {
      CL: <Icon_CL />,
      EEX: <Icon_EEX />,
    };
    return icon;
  }, []);

  return (
    <>
      <BookingDetailsContainer
        isFullRecap
        borderColor={theme.palette.LUX.obTokens?.bordersColorNeutralSecondary}
        id={'print-root'}
      >
        <CardTitleWrapper
          flexColumnInMobile={false}
          borderColor={theme.palette.LUX.obTokens?.bordersColorNeutralSecondary}
          padding="0.5rem 1rem"
        >
          <LuxTypography variant="heading4">
            {t('bookingDetails.title')}
          </LuxTypography>
          {isFullRecap && (
            <ButtonWrapper>
              <LuxButton
                size="small"
                iconStart="DownloadIcon"
                variant="ghost"
                onClick={handlePrintClick}
                style={{ textDecoration: 'underline', minWidth: 'unset' }}
              >
                {isMobile ? '' : t('generalCta.downloadConfirm')}
              </LuxButton>
            </ButtonWrapper>
          )}
        </CardTitleWrapper>
        <BookingCard theme={theme}>
          {generalData && currentStep >= 0 && (
            <DetailSection
              borderColor={
                theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
              }
              isLast={!onlyTruePatients.find(p => p.patient?.appointmentType)}
            >
              <SubSectionTitle>
                <LuxTypography variant="textMedium">
                  {t('detailsSection.address')}
                </LuxTypography>
                {/* {!isFullRecap && (
                <LuxButton variant="ghost" iconStart="EditIcon" size="small">
                  {t('generalCta.edit')}
                </LuxButton>
              )} */}
              </SubSectionTitle>
              <AddressSection isFullRecap={isFullRecap}>
                <ColumnWrapper gap="0.25rem">
                  <LuxTypography variant="textMdRegular">
                    {generalData.name}
                  </LuxTypography>
                  <LuxTypography variant="textMdRegular">
                    {formatGeneralAddress(generalData, !!brandName)}
                  </LuxTypography>
                  <LuxTypography variant="textMdRegular">
                    {generalData.phoneNumber}
                  </LuxTypography>
                </ColumnWrapper>
                <AddressCtasWrapper>
                  <LuxButton
                    variant="ghost"
                    iconStart="LocationIcon"
                    size="small"
                    aria-label={`${t('generalCta.getDirections')} (opens in a new tab)`}
                    onClick={() =>
                      window.open(
                        `https://www.google.com/maps?q=${encodeURIComponent(formatGeneralAddress(generalData))}`,
                        '_blank',
                      )
                    }
                    style={{ textDecoration: 'underline' }}
                  >
                    <ButtonLabelWrapper>
                      {t('generalCta.getDirections')}
                    </ButtonLabelWrapper>
                  </LuxButton>
                  <LuxButton
                    variant="ghost"
                    iconStart="InformationIcon"
                    size="small"
                    aria-label={`${t('generalCta.moreInfos')} (opens booking details popup)`}
                    onClick={() => setIsMoreInfoOpen(true)}
                    style={{ textDecoration: 'underline' }}
                  >
                    <ButtonLabelWrapper>
                      {t('generalCta.moreInfos')}
                    </ButtonLabelWrapper>
                  </LuxButton>
                </AddressCtasWrapper>
              </AddressSection>
            </DetailSection>
          )}
          {onlyTruePatients.find(p => p.patient?.appointmentType) && (
            <DetailSection
              isLast={checkIfLastSection(StepsStep.APPOINTMENT_TYPE)}
              gap={'1rem'}
            >
              <SubSectionTitle>
                <LuxTypography variant="textMedium">
                  {t('detailsSection.appType')}
                </LuxTypography>
                {!hideEditButton && currentStep > 0 && !isRescheduling && (
                  <LuxButton
                    variant="ghost"
                    iconStart="EditIcon"
                    size="small"
                    onClick={() =>
                      onEditClick(
                        Urls.APPOINTMENT_TYPE,
                        StepsStep.APPOINTMENT_TYPE,
                      )
                    }
                  >
                    {t('generalCta.edit')}
                  </LuxButton>
                )}
              </SubSectionTitle>
              {onlyTruePatients.map((patient, index) => {
                if (patient.patient?.appointmentType) {
                  const appointmentIdTranslation =
                    patient.patient.appointmentType.idTranslation;
                  return (
                    <ColumnWrapper key={index} isRow={isDesktop}>
                      {onlyTruePatients.length > 1 && (
                        <PatientPill
                          customTextColor={getTextColor(
                            theme.palette.LUX.obTokens.bgColorBaseDefault,
                          )}
                          customColor={
                            theme.palette.LUX.obTokens.bgColorBaseDefault
                          }
                          customBorderColor={
                            theme.palette.LUX.obTokens
                              .bordersColorNeutralSecondary
                          }
                        >
                          <UserIcon />
                          <LuxTypography variant="textXs">
                            {getPatientName(index)}
                          </LuxTypography>
                        </PatientPill>
                      )}
                      <LuxSelectablePill
                        selectable={false}
                        text={
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            sx={{
                              background: 'transparent!important',
                            }}
                          >
                            {appTypeIcon[appointmentIdTranslation ?? ''] && (
                              <span style={{ flexShrink: 0 }}>
                                {appTypeIcon[appointmentIdTranslation ?? '']}
                              </span>
                            )}
                            <LuxTypography
                              variant="textMedium"
                              fontWeight={400}
                            >
                              {brandName && appointmentIdTranslation
                                ? t(
                                    `appointmentTypePage.${brandName}.${appointmentIdTranslation}.title`,
                                  )
                                : patient.patient.appointmentType.name}{' '}
                            </LuxTypography>
                          </Box>
                        }
                        customColor={
                          theme.palette.LUX.obTokens.bgColorBaseSecondary
                        }
                        customBorderColor={
                          theme.palette.LUX.obTokens
                            .bordersColorNeutralSecondary
                        }
                      />
                    </ColumnWrapper>
                  );
                } else {
                  return null;
                }
              })}
            </DetailSection>
          )}
          {showInsuranceSection &&
            onlyTruePatients.some(p => p.patient?.insurance) && (
              <InsuranceSection
                patients={onlyTruePatients}
                currentPatient={currentPatient}
                onEditClick={onEditClick}
                hideEditButton={
                  hideEditButton ||
                  currentStep <= insuranceEditStep ||
                  isRescheduling
                }
                isLast={checkIfLastSection(StepsStep.INSURANCE)}
                isTyp
                hideInsuranceTypeLabel={!!brandName}
                editUrl={insuranceEditUrl}
                editStep={insuranceEditStep}
              />
            )}
          {onlyTruePatients.some(p => p.patient?.scheduleExam) && (
            <DetailSection
              isLast={checkIfLastSection(StepsStep.SCHEDULE_EXAM)}
              gap="1rem"
            >
              <SubSectionTitle>
                <LuxTypography variant="textMedium">
                  {t('detailsSection.scheduleExam')}
                </LuxTypography>
                {!hideEditButton && currentStep > 2 && !isRescheduling && (
                  <LuxButton
                    variant="ghost"
                    iconStart="EditIcon"
                    size="small"
                    onClick={() =>
                      onEditClick(Urls.SCHEDULE_EXAM, StepsStep.SCHEDULE_EXAM)
                    }
                  >
                    {t('generalCta.edit')}
                  </LuxButton>
                )}
              </SubSectionTitle>
              {onlyTruePatients.map((patient, index) => {
                if (patient.patient?.scheduleExam) {
                  return (
                    <ItemWrapper
                      key={index}
                      direction={isDesktop ? 'horizontal' : 'vertical'}
                    >
                      {onlyTruePatients.length > 1 && (
                        <PatientPill
                          customTextColor={getTextColor(
                            theme.palette.LUX.obTokens.bgColorBaseDefault,
                          )}
                          customColor={
                            theme.palette.LUX.obTokens.bgColorBaseDefault
                          }
                          customBorderColor={
                            theme.palette.LUX.obTokens
                              .bordersColorNeutralSecondary
                          }
                        >
                          <UserIcon />
                          <LuxTypography variant="textXs">
                            {getPatientName(index)}
                          </LuxTypography>
                        </PatientPill>
                      )}
                      <ScheduleDetailsWrapper typ>
                        <ScheduleWrapper>
                          <CalendarIcon />
                          <LuxTypography variant="textMedium" fontWeight={400}>
                            {dayjs(patient.patient.scheduleExam.day).format(
                              'MMM DD, YYYY',
                            )}
                          </LuxTypography>
                        </ScheduleWrapper>
                        <ScheduleWrapper>
                          <ClockIcon />
                          <LuxTypography variant="textMedium" fontWeight={400}>
                            {patient.patient?.scheduleExam.hour}
                          </LuxTypography>
                        </ScheduleWrapper>
                        {showDoctorDetails && (
                          <ScheduleWrapper>
                            <DoctorIcon />
                            <LuxTypography
                              variant="textMedium"
                              fontWeight={400}
                            >
                              {patient.patient?.scheduleExam.doctor}
                            </LuxTypography>
                            {patient.patient?.scheduleExam.slotId
                              ?.isRemoteDoctor && (
                              <LuxSelectablePill
                                selectable={false}
                                text={t('bookingDetails.remoteDoctor')}
                                color="info"
                              />
                            )}
                          </ScheduleWrapper>
                        )}
                      </ScheduleDetailsWrapper>
                    </ItemWrapper>
                  );
                } else {
                  return null;
                }
              })}
            </DetailSection>
          )}
          {(onlyTruePatients.find(
            p =>
              p.patient?.patientDetails && p.patient.patientDetails?.firstName,
          ) ||
            showAddPatientFrame) && (
            <PatientDetailsSection
              patients={onlyTruePatients}
              currentPatient={currentPatient}
              isFullRecap={isFullRecap}
              onEditClick={onEditClick}
              hideEditButton={
                hideEditButton || currentStep <= 3 || isRescheduling
              }
              isLast={checkIfLastSection(StepsStep.PATIENT_DETAILS)}
              formConfigsFromProps={onlineBookingStore.formConfigs}
              isTyp
            />
          )}
          {contactsDetails?.firstName && (
            <DetailSection isLast>
              <SubSectionTitle>
                <LuxTypography variant="textMedium">
                  {t('detailsSection.ctDetails')}
                </LuxTypography>
                {!hideEditButton && currentStep > 4 && !isRescheduling && (
                  <LuxButton
                    variant="ghost"
                    iconStart="EditIcon"
                    size="small"
                    onClick={() =>
                      onEditClick(
                        Urls.CONTACT_DETAILS,
                        StepsStep.CONTACT_DETAILS,
                      )
                    }
                  >
                    {t('generalCta.edit')}
                  </LuxButton>
                )}
              </SubSectionTitle>
              <InsPtCtDetailsWrapper halfWidth={isFullRecap}>
                <ColumnWrapper>
                  <ItemWrapper>
                    <LuxTypography variant="textMdRegular">
                      {t('fieldsLabel.name')}
                    </LuxTypography>
                    <LuxTypography variant="textMedium">
                      {contactsDetails.firstName} {contactsDetails.lastName}
                    </LuxTypography>
                  </ItemWrapper>
                  <ItemWrapper>
                    <LuxTypography variant="textMdRegular">
                      {t('fieldsLabel.email')}
                    </LuxTypography>
                    <LuxTypography variant="textMedium">
                      {contactsDetails.email}
                    </LuxTypography>
                  </ItemWrapper>
                </ColumnWrapper>
                {contactsDetails.phone?.number && (
                  <ColumnWrapper>
                    <ItemWrapper>
                      <LuxTypography variant="textMdRegular">
                        {t('fieldsLabel.phone')}
                      </LuxTypography>
                      <LuxTypography variant="textMedium">
                        {formatPhone(contactsDetails.phone)}
                      </LuxTypography>
                    </ItemWrapper>
                  </ColumnWrapper>
                )}
              </InsPtCtDetailsWrapper>
            </DetailSection>
          )}
        </BookingCard>
      </BookingDetailsContainer>
      <MoreInformationModal
        open={isMoreInfoOpen}
        onClose={() => setIsMoreInfoOpen(false)}
        generalData={generalData}
        onlineBookingStore={onlineBookingStore}
      />
    </>
  );
};

export default BookingDetailsTyp;
